import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FileUp, Info, Upload } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { notificar } from '@/shared/ui/avisos';
import {
  CampoDelPanel,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { mensajeDe } from './mensajes';
import type { ComprobanteImportado, Opcion } from './contrato';

const TAMANIO_MAXIMO = 2 * 1024 * 1024;

export function PanelDeImportacion({
  sustentos,
  alCerrar,
  alImportar,
}: {
  sustentos: Opcion[];
  alCerrar: () => void;
  alImportar: (resultado: ComprobanteImportado) => void;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [sustento, setSustento] = useState('02');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);
  const selector = useRef<HTMLInputElement>(null);

  const importar = useMutation({
    mutationFn: () => {
      const formulario = new FormData();
      formulario.append('archivo', archivo as File);
      formulario.append('sustento', sustento);
      if (observaciones.trim()) formulario.append('observaciones', observaciones.trim());
      return api.subir<ComprobanteImportado>('/compras', formulario);
    },
    onSuccess: (r) => {
      notificar.exito(
        `${r.comprobante.tipoEtiqueta} ${r.comprobante.numero} de ${r.comprobante.proveedorRazonSocial}`,

        r.completado ? 'Comprobante completado con su XML' : 'Comprobante registrado',
      );
      if (r.proveedorCreado) {
        notificar.exito(
          `${r.comprobante.proveedorRazonSocial} se dio de alta con lo que dice el comprobante`,
          'Proveedor nuevo',
        );
      }
      if (r.noCuadra) {
        notificar.error(
          'Sus bases no suman el subtotal que declara: revíselo antes de pagarlo',
          'El comprobante no cuadra',
        );
      }
      alImportar(r);
    },
    onError: (causa) => {
      const mensaje = mensajeDe(causa, 'No se pudo importar');
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  function elegir(f: File | undefined) {
    setError(null);
    if (!f) return;

    if (!f.name.toLowerCase().endsWith('.xml')) {
      setError('EL ARCHIVO NO ES UN XML');
      return;
    }
    if (f.size > TAMANIO_MAXIMO) {
      setError('EL XML PASA DE 2 MB');
      return;
    }
    setArchivo(f);
  }

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      icono={Upload}
      titulo="Importar comprobante"
      descripcion="El XML autorizado del proveedor · es el documento que vale"
      alEnviar={(evento) => {
        evento.preventDefault();
        if (archivo) {
          importar.mutate();
        }
      }}
      enviando={importar.isPending}
    >
      <div className="space-y-5">
        <div>
          <span className="text-sm font-semibold uppercase text-armazon-900">
            XML del comprobante
          </span>
          <input
            ref={selector}
            type="file"
            accept=".xml,text/xml,application/xml"
            className="hidden"
            onChange={(e) => elegir(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => selector.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              elegir(e.dataTransfer.files?.[0]);
            }}
            className={`mt-2 flex w-full items-center gap-3 rounded-xl border border-dashed px-4 py-6 text-left text-xs transition-colors ${
              archivo
                ? 'border-marca-300 bg-marca-50 text-marca-800'
                : 'border-armazon-300 text-armazon-500 hover:bg-armazon-50'
            }`}
          >
            <FileUp className="size-5 shrink-0" aria-hidden />
            <span className="min-w-0">
              {archivo ? (
                <>
                  <span className="block truncate font-semibold">{archivo.name}</span>
                  <span className="block text-[11px]">{(archivo.size / 1024).toFixed(0)} KB</span>
                </>
              ) : (
                <>
                  <span className="block font-semibold uppercase">
                    Elija el XML o suéltelo aquí
                  </span>
                  <span className="block text-[11px] uppercase">
                    Vale el autorizado del SRI o el comprobante suelto
                  </span>
                </>
              )}
            </span>
          </button>
        </div>

        <CampoDelPanel
          etiqueta="Sustento tributario"
          requerido
          ayuda="Decide si el IVA es crédito tributario o el valor es solo gasto"
        >
          <select
            value={sustento}
            onChange={(e) => setSustento(e.target.value)}
            className={claseEntradaPanel}
          >
            {sustentos.map((s) => (
              <option key={s.valor} value={s.valor}>
                {s.valor} · {s.etiqueta.toLocaleUpperCase('es')}
              </option>
            ))}
          </select>
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Observaciones">
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            maxLength={1000}
            rows={2}
            className={`${claseEntradaPanel} h-auto py-2`}
          />
        </CampoDelPanel>

        <p className="flex gap-3 rounded-xl border border-armazon-200 bg-armazon-50/60 p-4 text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Si el proveedor no está dado de alta, se crea con lo que dice el comprobante: el XML
            viene autorizado por el SRI. Se comprueba además que el comprobante esté dirigido a
            EPAGAL y que no esté ya registrado.
          </span>
        </p>

        {(error ?? (archivo ? null : 'Elija el XML del comprobante')) && (
          <p
            role={error ? 'alert' : undefined}
            className={`text-[11px] font-semibold uppercase ${
              error ? 'text-red-700' : 'text-armazon-500'
            }`}
          >
            {error ?? 'Elija el XML del comprobante'}
          </p>
        )}
      </div>
    </PanelLateral>
  );
}
